// import React from 'react';
/* eslint-disable camelcase */
import Auth, { FetchAPI, ParamsSerializer, Mutex } from './auth-service';
import { get as _get } from 'lodash';
import { SaveFile } from '../libraries/helpers';

import AlertsService from './alerts-service';
import MetadataService from './metadata-service';
import MC2HealthDataService from './mc2health-service';

class DashboardDataService {
    constructor () {
        if (!DashboardDataService.instance) {
            DashboardDataService.instance = this;
        }

        const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

        this.subscriptions = {};
        this.fetchMux = new Mutex();

        this.subscribe = (id, req, callback) => {
            console.debug('subscribe widget', id);
            const runnow = _get(this.subscriptions, `${id}.deleted`, true);

            // cancel any pending requests
            if (this.subscriptions[id]) {
                this.subscriptions[id].controller && this.subscriptions[id].controller.abort();
                this.subscriptions[id].controller = undefined;
            }
            this.subscriptions[id] = { id, req, callback };
            if (runnow) {
                this.subscriptions[id].controller = new AbortController();
                req(this.subscriptions[id].controller).then(callback);
            }
        };
        this.unsubscribe = id => {
            console.debug('unsubscribe widget', id);
            // soft delete because we're looping through this list right now
            if (this.subscriptions[id]) {
                this.subscriptions[id].deleted = true;
                // cancel any current pending requests
                this.subscriptions[id].controller && this.subscriptions[id].controller.abort();
                this.subscriptions[id].controller = undefined;
            }
        };

        // pass interval in secnods
        this.start = (interval) => {
            this.run = (new Date()).getTime();
            this.interval = interval * 1000;
            console.debug('start dashboard, interval', this.interval);
            const current = this.run;
            setTimeout(() => {
                this.fetchData(current);
            }, this.interval);
        };

        this.stop = () => {
            console.debug('stop dashboard');
            this.run = 0;
        };

        // fetch one widget at a time and restart timer after they are all done
        // the mutex is there to prevent multiple start cycles being called
        this.fetchData = async (run) => {
            let count = 0;
            const unlock = await this.fetchMux.lock();
            // dashboard has been updated since that request
            if (!this.run || this.run !== run) {
                unlock();
                return;
            }

            const doFetch = i => {
                const subs = Object.values(this.subscriptions);
                if (i >= subs.length) {
                    unlock();
                    // we're done. run again
                    const current = this.run;
                    console.debug("we're done. run again soon", this.interval, current);
                    setTimeout(() => {
                        console.debug('doFetch done with', count, 'calls', this.interval, current);
                        this.fetchData(current);
                    }, this.interval);
                    return;
                }

                const sub = subs[i];
                if (sub.deleted) {
                    doFetch(i + 1);
                    return;
                }

                sub.controller && sub.controller.abort();
                sub.controller = new AbortController();
                sub.req(sub.controller).then(res => {
                    count++;
                    doFetch(i + 1);
                    return res;
                }).then(res => {
                    if (!sub.deleted) sub.callback(res);
                });
            };

            doFetch(0);
        };

        this.print = dash => FetchAPI({
            method: 'POST',
            url: `dashboards/${dash.uuid}/-print`,
            headers: { 'Content-type': 'application/pdf' },
            responseType: 'arraybuffer'
        }).then(
            res => {
                if (!res) {
                    console.error('cannot print, empty response from server');
                }
                const asof = (new Date()).toISOString();
                const name = `${dash.title}_${asof}.pdf`.split(' ').join('_');
                SaveFile(res, name, 'application/pdf');
            },
            err => console.error('error downloading dashboard report', err)
        );

        this.list = () => FetchAPI({
            method: 'GET',
            url: 'dashboards/'
        });

        this.update = dash => FetchAPI({
            method: 'PUT',
            url: `dashboards/${encodeURIComponent(dash.uuid)}/`,
            body: dash
        });

        this.create = dash => FetchAPI({
            method: 'POST',
            url: 'dashboards/',
            body: dash
        });

        this.delete = uuid => FetchAPI({
            method: 'DELETE',
            url: `dashboards/${encodeURIComponent(uuid)}/`
        });

        this.txfr = user => FetchAPI({
            method: 'PUT',
            url: `dashboards/${encodeURIComponent(user)}/-txfr`
        });

        this.report = (uuid, dash) => FetchAPI({
            method: 'POST',
            url: `dashboards/${encodeURIComponent(uuid)}/-report`,
            body: dash
        });

        this.next = (uuid, dash) => FetchAPI({
            method: 'POST',
            url: `dashboards/${encodeURIComponent(uuid)}/next`,
            body: dash
        });

        // get data for widgets
        this.getHealthAlertsData = ({ type, uuids }, controller) => {
            if (!controller) return Promise.resolve();
            const reqs = [];
            const SystemHealth = Auth.permissions.get('SystemHealth');
            const Sensors = Auth.permissions.get('Sensors');

            if (type !== 'CMC' && Sensors.read) {
                reqs.push({
                    url: `widgets/sensorsissues/?${ParamsSerializer({ uuids })}`,
                    handler: res => (res || [])
                        .reduce((a, c) => {
                            const issues = _get(c, 'health_metrics.issues', []) || [];
                            return a.concat(issues
                                .map(issue => ({
                                    ...issue,
                                    name: _get(c, 'data.name') || c.host,
                                    uuid: c.uuid,
                                    type: 'Sensor',
                                })));
                        }, [])
                });
            }
            if (type !== 'Sensor' && SystemHealth.read) {
                reqs.push({
                    url: 'system/health',
                    handler: res => _get(res, 'object.health_metrics.issues', [])
                        .map(issue => ({
                            ...issue,
                            type: 'CMC',
                        }))
                });
            }
            return Promise.all(reqs.map(req => {
                return FetchAPI({ url: req.url, signal: controller.signal }).then(req.handler);
            })).then(res => {
                return (res || []).reduce((a, c) => a.concat(c), []);
            });
        };

        this.getAlertGroups = (p, controller) => {
            if (!controller) return Promise.resolve();
            return AlertsService.getAlertGroups(p, controller).then(res => {
                return (res || []).map(a => {
                    const groupData = a => {
                        a.groups = p.group.map(g => {
                            return { key: g, value: _get(a, (AlertsService.columnsMap[g] || {}).field) };
                        });
                        return {
                            count: a.count,
                            groups: p.group.map(g => (
                                {
                                    key: g,
                                    value: _get(a, (AlertsService.columnsMap[g] || {}).field)
                                })),
                            key: AlertsService.key(a),
                            value: a.groups.map(g => g.value),
                            timeline: a.timeline,
                        };
                    };
                    if (MC2CHECK) {
                        return {
                            ...a,
                            count: a.count,
                            groups: (a.groups || []).map(groupData)
                        };
                    } else return groupData(a);
                });
            });
        };

        this.getPolicyAlerts = (p, controller) => {
            if (!controller) return Promise.resolve();
            return AlertsService.getPolicyAlerts(p, controller);
        };

        this.getMtdtTimelineGroups = p => {
            return MetadataService.getMtdtTimelineGroups(p);
        };

        this.getMtdtGroups = (p, controller) => {
            if (!controller) return Promise.resolve();
            return MetadataService.getMtdtGroups(p, controller).then(res => {
                return (res || [])
                    .map(a => ({
                        ..._get(a, 'latest[0]', {}),
                        ...{ groups: a.keys, key: MetadataService.key(a), count: a.count },
                    }));
            });
        };

        this.getAlertTimelineSeries = p => {
            return AlertsService.getAlertTimelineSeries(p);
        };

        this.getMtdtTimelineSeries = (p, controller) => {
            if (!controller) return Promise.resolve();
            return MetadataService.getMtdtTimelineSeries(p, controller);
        };

        this.getUserAgents = (p) => {
            return MetadataService.getUserAgents(p);
        };

        // only available in MC2 api
        this.mc2GetAlertCounts = (p, controller) => {
            if (!controller) return Promise.resolve();
            return AlertsService.mc2GetAlertCounts(p, controller);
        };

        this.getMtdtCounts = (p, controller) => {
            if (!controller) return Promise.resolve();
            return MetadataService.getMtdtCounts(p, controller);
        };

        this.mc2GetHealthAlerts = ({ type, target_cmc, target_sensors }, controller) => {
            if (!controller) return Promise.resolve();
            const params = {
                type,
                target_cmc,
                target_sensors,
            };
            const reqs = [];
            const SystemHealth = Auth.permissions.get('SystemHealth');
            const CMC = Auth.permissions.get('CMC');
            if (type !== 'MC2' && CMC.read) {
                reqs.push({
                    req: () => FetchAPI({
                        url: `mc2/widgets/healthalerts/?${ParamsSerializer(params)}`,
                        signal: controller.signal,
                    }),
                    handler: res => res,
                });
            }
            if ((type === 'MC2' || type === 'All') && SystemHealth.read) {
                reqs.push({
                    req: MC2HealthDataService.getSystemHealth,
                    handler: res => {
                        return _get(res, 'object.health_metrics.issues', [])
                            .map(issue => ({
                                ...issue,
                                type: 'MC2',
                            }));
                    },
                });
            }
            return Promise.all(reqs.map(req => {
                return req.req().then(req.handler);
            })).then(res => (res || []).reduce((a, c) => c ? a.concat(c) : a, []));
        };

        // cmc is name, sensor is uuid
        this.mc2GetSensorHealth = ({ cmc, sensor }, controller) => {
            if (!controller) return Promise.resolve();
            return FetchAPI({
                method: 'GET',
                url: `mc2/sensors/${cmc}/${sensor}/`,
                signal: controller.signal,
            });
        };

        // cmc is uuid (to confuse the enemy)
        this.mc2GetCMCHealth = ({ cmc }, controller) => {
            if (!controller) return Promise.resolve();
            return FetchAPI({
                method: 'GET',
                url: `mc2/cmc/${cmc}/`,
                signal: controller.signal,
            });
        };
    }
}

const DashboardService = new DashboardDataService();

export {
    DashboardService as default,
};
